import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "@clerk/clerk-react";
import {
  Document,
  Page,
  StyleSheet,
  Text,
  View,
  pdf,
} from "@react-pdf/renderer";
import adminStyle from "../../../styles/dashboard-admin.module.scss";
import { getServerURL } from "../../../utils/funcs/get-serverURL";
import { BottomSpacer } from "../../spacer";

type SavedInvoice = {
  id: string;
  invoiceNumber: number;
  invoiceDate: string;
  dueDate: string;
  appointmentDateTimeRaw: string;
  customerName: string;
  propertyAddress: string;
  category: string;
  quantity: number;
  rate: number;
  subtotal: number;
  total: number;
  filename: string;
  createdAt?: string;
};

const INPUT_PATTERN = /^Date & Time:\s*.+\r?\nCustomer:\s*.+\r?\nProperty Address:\s*.+\r?\nCategory:\s*.+\r?\nBudget:\s*£\s*[0-9]+(?:\.[0-9]{1,2})?\s*$/;

const pdfStyles = StyleSheet.create({
  page: {
    backgroundColor: "#f3f7f8",
    color: "#1f2933",
    fontFamily: "Helvetica",
    fontSize: 11,
    paddingTop: 36,
    paddingBottom: 40,
    paddingHorizontal: 38,
  },
  shell: {
    backgroundColor: "#ffffff",
    borderRadius: 16,
    padding: 28,
    border: "1 solid #d8e3e7",
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 24,
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    letterSpacing: 1.4,
    color: "#0f5f66",
    marginBottom: 12,
  },
  companyName: {
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 4,
  },
  companyLine: {
    color: "#52606d",
    marginBottom: 2,
  },
  metaCard: {
    backgroundColor: "#eef7f8",
    borderRadius: 12,
    padding: 14,
    minWidth: 180,
  },
  metaLabel: {
    fontSize: 9,
    textTransform: "uppercase",
    color: "#7b8794",
    marginBottom: 2,
  },
  metaValue: {
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 10,
    color: "#102a43",
  },
  sectionRow: {
    flexDirection: "row",
    marginBottom: 24,
  },
  sectionCard: {
    width: "100%",
    backgroundColor: "#f8fbfc",
    borderRadius: 12,
    padding: 16,
    border: "1 solid #e4edf0",
  },
  sectionLabel: {
    fontSize: 9,
    textTransform: "uppercase",
    color: "#829ab1",
    marginBottom: 10,
    letterSpacing: 0.6,
  },
  billToName: {
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 5,
  },
  line: {
    color: "#486581",
    marginBottom: 3,
    lineHeight: 1.5,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#0f5f66",
    color: "#ffffff",
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 14,
    fontSize: 10,
    fontWeight: 700,
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderBottom: "1 solid #e4edf0",
    backgroundColor: "#ffffff",
  },
  itemName: {
    flex: 2.2,
  },
  qty: {
    flex: 0.7,
    textAlign: "center",
  },
  rate: {
    flex: 1,
    textAlign: "right",
  },
  amount: {
    flex: 1,
    textAlign: "right",
  },
  totalsWrap: {
    marginTop: 16,
    alignSelf: "flex-end",
    width: 220,
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 7,
    borderBottom: "1 solid #e4edf0",
  },
  totalLabel: {
    color: "#52606d",
  },
  totalValue: {
    fontWeight: 700,
  },
  grandTotalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 10,
    backgroundColor: "#eef7f8",
    marginTop: 12,
  },
  grandTotalLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: "#0f5f66",
  },
  grandTotalValue: {
    fontSize: 12,
    fontWeight: 700,
    color: "#0f5f66",
  },
  footerGrid: {
    flexDirection: "row",
    gap: 16,
    marginTop: 28,
  },
  footerCard: {
    flexGrow: 1,
    flexBasis: 0,
    borderRadius: 12,
    border: "1 solid #e4edf0",
    padding: 16,
    backgroundColor: "#ffffff",
  },
  footerTitle: {
    fontSize: 10,
    textTransform: "uppercase",
    color: "#7b8794",
    marginBottom: 10,
  },
  footerText: {
    color: "#486581",
    lineHeight: 1.6,
  },
});

const formatCurrency = (value: number) => `£${value.toFixed(2)}`;

function InvoiceDocument({ invoice }: { invoice: SavedInvoice }) {
  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.shell}>
          <View style={pdfStyles.headerRow}>
            <View>
              <Text style={pdfStyles.title}>INVOICE</Text>
              <Text style={pdfStyles.companyName}>Teck Engineering Ltd</Text>
              <Text style={pdfStyles.companyLine}>Teck Engineering</Text>
              <Text style={pdfStyles.companyLine}>Paul St, London EC2A 4NE, UK</Text>
              <Text style={pdfStyles.companyLine}>United Kingdom</Text>
            </View>

            <View style={pdfStyles.metaCard}>
              <Text style={pdfStyles.metaLabel}>Invoice#</Text>
              <Text style={pdfStyles.metaValue}>{invoice.invoiceNumber}</Text>
              <Text style={pdfStyles.metaLabel}>Invoice Date</Text>
              <Text style={pdfStyles.metaValue}>{invoice.invoiceDate}</Text>
              <Text style={pdfStyles.metaLabel}>Due Date</Text>
              <Text style={pdfStyles.metaValue}>{invoice.dueDate}</Text>
            </View>
          </View>

          <View style={pdfStyles.sectionRow}>
            <View style={pdfStyles.sectionCard}>
              <Text style={pdfStyles.sectionLabel}>Bill To</Text>
              <Text style={pdfStyles.billToName}>{invoice.customerName}</Text>
              <Text style={pdfStyles.line}>{invoice.propertyAddress}</Text>
              <Text style={pdfStyles.line}>United Kingdom</Text>
            </View>
          </View>

          <View>
            <View style={pdfStyles.tableHeader}>
              <Text style={pdfStyles.itemName}>Item Name</Text>
              <Text style={pdfStyles.qty}>Quantity</Text>
              <Text style={pdfStyles.rate}>Rate</Text>
              <Text style={pdfStyles.amount}>Amount</Text>
            </View>

            <View style={pdfStyles.tableRow}>
              <Text style={pdfStyles.itemName}>{invoice.category}</Text>
              <Text style={pdfStyles.qty}>{invoice.quantity}</Text>
              <Text style={pdfStyles.rate}>{invoice.rate.toFixed(2)}</Text>
              <Text style={pdfStyles.amount}>{invoice.total.toFixed(2)}</Text>
            </View>
          </View>

          <View style={pdfStyles.totalsWrap}>
            <View style={pdfStyles.totalRow}>
              <Text style={pdfStyles.totalLabel}>Subtotal</Text>
              <Text style={pdfStyles.totalValue}>{invoice.subtotal.toFixed(2)}</Text>
            </View>
            <View style={pdfStyles.grandTotalRow}>
              <Text style={pdfStyles.grandTotalLabel}>Total</Text>
              <Text style={pdfStyles.grandTotalValue}>{formatCurrency(invoice.total)}</Text>
            </View>
          </View>

          <View style={pdfStyles.footerGrid}>
            <View style={pdfStyles.footerCard}>
              <Text style={pdfStyles.footerTitle}>Notes</Text>
            </View>
            <View style={pdfStyles.footerCard}>
              <Text style={pdfStyles.footerTitle}>Bank details</Text>
              <Text style={pdfStyles.footerText}>Mujahid Mohammed</Text>
              <Text style={pdfStyles.footerText}>Sort code: 04-06-05</Text>
              <Text style={pdfStyles.footerText}>Account number: 20106870</Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
}

export default function InvoiceGenerator() {
  const { getToken } = useAuth();
  const serverURL = getServerURL();
  const [rawInput, setRawInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [invoices, setInvoices] = useState<SavedInvoice[]>([]);

  const hasInputText = rawInput.trim().length > 0;
  const isInputValid = useMemo(() => INPUT_PATTERN.test(rawInput.trim()), [rawInput]);

  const downloadInvoicePdf = async (invoice: SavedInvoice) => {
    const blob = await pdf(<InvoiceDocument invoice={invoice} />).toBlob();
    const blobUrl = URL.createObjectURL(blob);
    const link = window.document.createElement("a");
    link.href = blobUrl;
    link.download = invoice.filename;
    window.document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);
  };

  useEffect(() => {
    let isActive = true;

    const loadInvoices = async () => {
      try {
        setIsLoadingInvoices(true);
        const token = await getToken();
        if (!token) {
          throw new Error("Unable to verify the admin session.");
        }

        const response = await axios.get(`${serverURL}/admin/invoices`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (isActive) {
          setInvoices(response.data.invoices || []);
        }
      } catch (error: any) {
        if (isActive) {
          setErrorMessage(error?.response?.data?.error || error?.message || "Failed to load invoices.");
        }
      } finally {
        if (isActive) {
          setIsLoadingInvoices(false);
        }
      }
    };

    loadInvoices();

    return () => {
      isActive = false;
    };
  }, [getToken, serverURL]);

  const handleGenerateInvoice = async () => {
    if (!isInputValid) {
      setErrorMessage("Use the exact five-line format shown below before generating the invoice.");
      setSuccessMessage("");
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage("");
      setSuccessMessage("");

      const token = await getToken();
      if (!token) {
        throw new Error("Unable to verify the admin session.");
      }

      const response = await axios.post(
        `${serverURL}/admin/invoices`,
        { rawInput: rawInput.trim() },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const invoice: SavedInvoice = response.data.invoice;
      setInvoices((currentInvoices) => [invoice, ...currentInvoices.filter((currentInvoice) => currentInvoice.id !== invoice.id)]);

      try {
        await downloadInvoicePdf(invoice);
        setRawInput("");
        setSuccessMessage(`Invoice #${invoice.invoiceNumber} saved and downloaded as ${invoice.filename}.`);
      } catch (pdfError) {
        console.error("Failed to generate invoice PDF", pdfError);
        setSuccessMessage(`Invoice #${invoice.invoiceNumber} was saved, but the PDF download failed in the browser.`);
      }
    } catch (error: any) {
      console.error("Failed to create invoice", error);
      setErrorMessage(error?.response?.data?.error || error?.message || "Failed to create invoice.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={adminStyle.invoiceCard}>
      <div className={adminStyle.invoiceHeader}>
        
          <h3>Invoice Generator</h3>
          <p className={adminStyle.invoiceLead}>
            Paste one appointment block, save it to MongoDB, and download a matching invoice PDF.
          </p>
      </div>

      <label className={adminStyle.invoiceLabel} htmlFor="invoice-generator-input">
        Appointment Info
      </label>
      <textarea
        id="invoice-generator-input"
        className={adminStyle.invoiceTextarea}
        value={rawInput}
        onChange={(event) => setRawInput(event.target.value)}
        spellCheck={false}
      />

      {hasInputText && (
        <div className={adminStyle.invoiceHintRow}>
          <span className={isInputValid ? adminStyle.invoiceHintValid : adminStyle.invoiceHintInvalid}>
            {isInputValid ? "Format valid" : "Input must stay in the exact five-line structure"}
          </span>
        </div>
      )}

      <div className={adminStyle.invoiceActions}>
        <button
          type="button"
          className={adminStyle.activeButton}
          disabled={isSubmitting}
          onClick={handleGenerateInvoice}
        >
          {isSubmitting ? "Saving and building PDF..." : "Create Invoice PDF"}
        </button>
      </div>

      {hasInputText && errorMessage && <p className={`${adminStyle.invoiceAlert} ${adminStyle.invoiceAlertError}`}>{errorMessage}</p>}
      {hasInputText && successMessage && <p className={`${adminStyle.invoiceAlert} ${adminStyle.invoiceAlertSuccess}`}>{successMessage}</p>}

      <div className={adminStyle.invoiceTableCard}>
        <div className={adminStyle.invoiceTableHeader}>
          <h4>Generated invoices</h4>
          <span>{invoices.length} total</span>
        </div>

        {isLoadingInvoices ? (
          <p className={adminStyle.invoiceTableEmpty}>Loading invoices...</p>
        ) : invoices.length === 0 ? (
          <p className={adminStyle.invoiceTableEmpty}>No invoices have been generated yet.</p>
        ) : (
          <div className={adminStyle.invoiceTableWrap}>
            <table className={adminStyle.invoiceTable}>
              <thead>
                <tr>
                  <th>Invoice#</th>
                  <th>Invoice Date</th>
                  <th>Customer</th>
                  <th>Address</th>
                  <th>Category</th>
                  <th>Total</th>
                  <th>File</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>{invoice.invoiceNumber}</td>
                    <td>{invoice.invoiceDate}</td>
                    <td>{invoice.customerName}</td>
                    <td>{invoice.propertyAddress}</td>
                    <td>{invoice.category}</td>
                    <td>{formatCurrency(invoice.total)}</td>
                    <td>{invoice.filename}</td>
                    <td>
                      <button
                        type="button"
                        className={adminStyle.invoiceTableButton}
                        onClick={() => {
                          downloadInvoicePdf(invoice).catch((pdfError) => {
                            console.error("Failed to download invoice PDF", pdfError);
                            setErrorMessage("Failed to download invoice PDF.");
                          });
                        }}
                      >
                        Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <BottomSpacer />
    </div>
  );
}